import { Component, Input, OnInit } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { MapDevice } from '../../../core/models';

@Component({
  selector: 'app-device-detail-modal',
  templateUrl: './device-detail-modal.component.html',
  styleUrls: ['./device-detail-modal.component.scss']
})
export class DeviceDetailModalComponent implements OnInit {
  static readonly MODAL_ID = 8201;

  @Input() device: MapDevice;
  @Input() title: string = 'Device Details';
  activeTab: 'details' | 'raw' = 'details';

  // Pre-computed values
  azimuthText: string | null = null;
  tiltText: string | null = null;
  azimuthBeamWidthText: string | null = null;
  elevationBeamWidthText: string | null = null;
  rangeText: string | null = null;
  googleMapsUrl: string | null = null;
  updatedAtText: string = '';
  rawDataEntries: Array<{key: string, value: any}> = [];

  constructor(public modalRef: BsModalRef) {}

  static show(modalService: BsModalService, initialState: Partial<DeviceDetailModalComponent>) {
    return modalService.show(DeviceDetailModalComponent, {
      class: 'modal-md',
      id: DeviceDetailModalComponent.MODAL_ID,
      initialState
    });
  }

  ngOnInit() {
    this.computeAllValues();
  }

  private computeAllValues() {
    // Azimuth
    const azimuthValue = this.device?.azimuth ?? this.device?.azimuthDeg;
    if (azimuthValue !== undefined && azimuthValue !== null) {
      const num = Number(azimuthValue);
      if (isFinite(num)) {
        this.azimuthText = num.toFixed(2);
      }
    }

    // Tilt
    const t = this.device?.tilt;
    if (t !== undefined && t !== null) {
      const dir = t > 0 ? 'Upward' : t < 0 ? 'Downward' : '';
      this.tiltText = `${Math.abs(Number(t)).toFixed(2)}°${dir ? ' ' + dir : ''}`;
    }

    // Azimuth Beam Width
    const azBeamWidth = this.device?.azimuth_beam_width;
    if (azBeamWidth !== undefined && azBeamWidth !== null) {
      const num = Number(azBeamWidth);
      if (isFinite(num)) {
        this.azimuthBeamWidthText = num.toFixed(2);
      }
    }

    // Elevation Beam Width
    const elBeamWidth = this.device?.elevation_beam_width;
    if (elBeamWidth !== undefined && elBeamWidth !== null) {
      const num = Number(elBeamWidth);
      if (isFinite(num)) {
        this.elevationBeamWidthText = num.toFixed(2);
      }
    }

    // Range
    const range = this.device?.range_meters;
    if (range !== undefined && range !== null) {
      const num = Number(range);
      if (isFinite(num)) {
        this.rangeText = num.toFixed(2);
      }
    }

    // Google Maps URL
    const lat = this.device?.latitude;
    const lng = this.device?.longitude;
    if (lat != null && lng != null) {
      this.googleMapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
    }

    // Updated At
    const updatedAt = this.device?.data?.updatedAt;
    if (updatedAt !== undefined && updatedAt !== null) {
      const ms = Number(updatedAt);
      if (isFinite(ms) && ms > 0) {
        try {
          this.updatedAtText = new Date(ms).toLocaleString();
        } catch {
          this.updatedAtText = '';
        }
      }
    }

    // Raw Data Entries - convert to plain object if it's an RxDB document
    if (this.device?.data) {
      const plainData = (this.device.data && 'toJSON' in this.device.data && typeof this.device.data.toJSON === 'function')
        ? this.device.data.toJSON()
        : this.device.data;
      this.rawDataEntries = Object.entries(plainData).map(([key, value]) => ({key, value}));
    }
  }

  close() {
    this.modalRef.hide();
  }

  setActiveTab(tab: 'details' | 'raw') {
    this.activeTab = tab;
  }
}
